// React i hooks
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

// Navigacija
import { Routes } from "../../navigation";
import { Routes as FleetVehicleRoutes } from "ui/modules/fleet/vehicles/navigation";

// Helpers
import { transformRoute, useTranslate } from "helpers";

// API
import { getFleetVehiclesAPI } from "services/api/fleet/vehicles/api";
import {
  FleetVehicleRow,
  FleetVehicleSearchFormValues,
} from "services/api/fleet/vehicles/types";
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { initialFleetVehicleSearchForm } from "services/api/fleet/vehicles/consts";

// Komponente
import { VehicleSearchPageCmp } from "./types";
import { Breadcrumb } from "ui/components/Breadcrumb";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { Pagination } from "services/search";
import { NoResults } from "ui/components/NoResults";
import { Button } from "ui/components/Button/Button";
import { Badge } from "ui/components/Badge";
import { ButtonMenu } from "ui/components/Button/ButtonMenu";
import ButtonMenuItem from "ui/components/Button/ButtonMenu/ButtonMenuItem";
import ButtonGroup from "ui/components/Button/ButtonMenu/ButtonGroup";
import useQueryParam from "services/hooks/useQueryParam";

// Stranice / Komponente pretrage
import VehicleSearchResults from "./components/VehicleSearchResults/VehicleSearchResults";
import VehicleSearchFilters from "./components/VehicleSearchFilters/VehicleSearchFilters";
import { VehicleExportModal } from "./components/VehicleExportModal";
import { VehicleImportModal } from "./components/VehicleImportModal";
import { VehicleJomImportModal } from "./components/VehicleJomImportModal";
import { VehicleResidualValueModal } from "./components/VehicleResidualValueModal";
import { VehicleNewPriceModal } from "./components/VehicleNewPriceModal";
import { VehicleUsedPriceModal } from "./components/VehicleUsedPriceModal";

// Konstante
import { FLEET_VEHICLE_SEARCH_PAGE_TITLE } from "ui/components/PageTitle/consts";
import VehicleEditCard from "../VehicleDetailPage/components/VehicleDetailCard/components/VehicleEditCard/VehicleEditCard";

// Hooks
import { useSelectedStringItemsChanges } from "./hooks";

const VehicleSearchPage: VehicleSearchPageCmp = () => {
  const { t } = useTranslate();

  const [searchResults, setSearchResults] = useState<
    DataWithPagination<FleetVehicleRow>
  >(initialDataWithPagination);

  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  // Podaci odabranih vozila akumulirani kroz stranice pretrage — da izvoz vidi sva odabrana
  // vozila (a ne samo trenutnu stranicu) i da paginacija u izvozu radi.
  const [selectedVehiclesMap, setSelectedVehiclesMap] = useState<
    Record<string, FleetVehicleRow>
  >({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);
  const [showExportModal, setShowExportModal] = useState<boolean>(false);
  const [showImportModal, setShowImportModal] = useState<boolean>(false);
  const [showJomImportModal, setShowJomImportModal] = useState<boolean>(false);
  const [showResidualValueModal, setShowResidualValueModal] = useState<boolean>(false);
  const [showNewPriceModal, setShowNewPriceModal] = useState<boolean>(false);
  const [showUsedPriceModal, setShowUsedPriceModal] = useState<boolean>(false);
  const [viewType, setViewType] = useQueryParam("view", "table", ["table", "grid"] as const);
  const [currentFilters, setCurrentFilters] = useState<FleetVehicleSearchFormValues>(initialFleetVehicleSearchForm);
  const searchRequestIdRef = useRef(0);
  const navigate = useNavigate();

  const fetchSearchResult = async (
    pagination: Pagination,
    filters: FleetVehicleSearchFormValues = currentFilters
  ) => {
    const requestId = ++searchRequestIdRef.current;

    // Nijedan status nije odabran — nijedno vozilo ne zadovoljava filter, pa nema
    // smisla ni zvati API (prazna lista se na backendu tumači kao "bez filtera").
    if (filters.statuses.length === 0) {
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const results = await getFleetVehiclesAPI(
        pagination,
        filters.search,
        filters.vehicleManufacturerId,
        filters.vehicleModelId,
        filters.vehicleTypeId,
        null,
        null,
        null,
        {
          vinNumber: filters.vinNumber,
          firstRegistrationDateFrom: filters.firstRegistrationDateFrom,
          firstRegistrationDateTo: filters.firstRegistrationDateTo,
          registrationPlate: filters.registrationPlate,
          registrationExpirationFrom: filters.registrationExpirationFrom,
          registrationExpirationTo: filters.registrationExpirationTo,
          kwFrom: filters.kwFrom,
          kwTo: filters.kwTo,
          kmTraveledFrom: filters.kmTraveledFrom,
          kmTraveledTo: filters.kmTraveledTo,
          condition: filters.condition,
          fuelType: filters.fuelType,
          statuses: filters.statuses,
          officeId: filters.officeId,
        }
      );

      if (requestId !== searchRequestIdRef.current) return;
      setSearchResults(results);
    } catch {
      if (requestId !== searchRequestIdRef.current) return;
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    if (requestId !== searchRequestIdRef.current) return;
    setIsLoading(false);
  };

  const handleRowClick = (id: string) => {
    navigate(
      transformRoute(FleetVehicleRoutes.FleetVehicleDetailPageRouter, {
        id,
      })
    );
  };

  const handleAddNew = (event: React.MouseEvent<HTMLButtonElement>) => {
    setShowAddNewModal(true);
  };

  const handleEditSuccess = () => {
    fetchSearchResult(searchResults.pagination);
    setShowAddNewModal(false);
  };

  const handleSearchChange = (formValues: FleetVehicleSearchFormValues) => {
    setCurrentFilters(formValues);
    fetchSearchResult(searchResults.pagination, formValues);
  };

  const handleSelectedChange = useSelectedStringItemsChanges(
    selectedRows,
    setSelectedRows
  );

  // Drži samo trenutno odabrane; dopuni podatke iz trenutne stranice, zadrži cache za ostale stranice.
  useEffect(() => {
    setSelectedVehiclesMap((prev) => {
      const next: Record<string, FleetVehicleRow> = {};
      selectedRows.forEach((id) => {
        const data = searchResults.data.find((v) => v.id === id) ?? prev[id];
        if (data) next[id] = data;
      });
      return next;
    });
  }, [selectedRows, searchResults.data]);

  const handleOpenExportModal = () => {
    setShowExportModal(true);
  };

  const handleExportSuccess = () => {
    setShowExportModal(false);
    setSelectedRows([]);
  };

  const handleOpenImportModal = () => {
    setShowImportModal(true);
  };

  const handleImportSuccess = () => {
    setShowImportModal(false);
    fetchSearchResult(searchResults.pagination);
  };

  const handleOpenJomImportModal = () => {
    setShowJomImportModal(true);
  };

  const handleJomImportSuccess = () => {
    setShowJomImportModal(false);
    fetchSearchResult(searchResults.pagination);
  };

  const handlePaginationChange = (newPagination: Pagination) => {
    fetchSearchResult(newPagination);
  };

  return (
    <>
      <div className="">
        <div className="bg-white rounded-t-md border border-b mb-2">
          <PageHeader>
            <div className="flex items-center justify-between">
              <div>
                <PageTitle
                  title={FLEET_VEHICLE_SEARCH_PAGE_TITLE.text}
                  icon={FLEET_VEHICLE_SEARCH_PAGE_TITLE.icon}
                  iconColor={FLEET_VEHICLE_SEARCH_PAGE_TITLE.iconColor}
                />
                <Breadcrumb />
              </div>
              <div className="flex items-center gap-2">
                {selectedRows.length > 0 && (
                  <Badge
                    text={`${t("admin.fleet.vehicle.export.selected.count")}: ${selectedRows.length}`}
                    variant="warning"
                  />
                )}
                <ButtonMenu
                  text={t("admin.fleet.vehicle.search.action.menu")}
                  size="full"
                  variant="secondary"
                  startIcon="/media/icons/plus.svg"
                  groupDirection="horizontal"
                >
                  <ButtonGroup text={t("admin.fleet.vehicle.search.action.export")}></ButtonGroup>
                  <ButtonMenuItem
                    startIcon="/media/icons/data-export.svg"
                    onClick={handleOpenExportModal}
                  >
                    {t("admin.fleet.vehicle.search.action.menu.export.xlsx")}
                    {selectedRows.length > 0 && (
                      <span className="w-5 h-5 flex items-center justify-center text-xs bg-yellow-500 text-white rounded-full absolute top-1/2 -translate-y-1/2 right-2">
                        {selectedRows.length}
                      </span>
                    )}
                  </ButtonMenuItem>
                  <ButtonGroup text={t("admin.fleet.vehicle.search.action.import")}></ButtonGroup>
                  <ButtonMenuItem
                    startIcon="/media/icons/sheet.svg"
                    onClick={handleOpenImportModal}
                  >
                    {t("admin.fleet.vehicle.search.action.menu.import.xlsx")}
                  </ButtonMenuItem>
                  <ButtonMenuItem
                    startIcon="/media/icons/database-search.svg"
                    onClick={handleOpenJomImportModal}
                  >
                    {t("admin.fleet.vehicle.search.action.menu.import.jom")}
                  </ButtonMenuItem>
                </ButtonMenu>
                <ButtonMenu
                  text="Izračun cijena"
                  size="full"
                  variant="secondary"
                  startIcon="/media/icons/plus.svg"

                >
                  <ButtonMenuItem
                    startIcon="/media/icons/price-used.svg"
                    onClick={() => setShowUsedPriceModal(true)}
                  >
                    Cijena rabljenog vozila
                  </ButtonMenuItem> 
                </ButtonMenu>
                <Button
                  variant="primary"
                  children={t("admin.vehicle.search.add")}
                  startIcon="/media/icons/plus.svg"
                  onClick={handleAddNew}
                />
              </div>
            </div>
          </PageHeader>
        </div>
        <div className="px-0 pt-2 mx-[-1px] xl:px-6">
          <VehicleSearchFilters
            onSearchChange={handleSearchChange}
            viewType={viewType}
            onViewTypeChange={(v) => setViewType(v as "table" | "grid")}
          />

          {!isLoading && searchResults.pagination.totalPages === 0 ? (
            <NoResults message={t("admin.vehicle.search.noresults")} />
          ) : (
            <VehicleSearchResults
              searchResults={searchResults}
              selectedRows={selectedRows}
              isLoading={isLoading}
              selectableRows={true}
              viewType={viewType}
              onRowClick={handleRowClick}
              onPaginationChange={handlePaginationChange}
              onSelectedChange={handleSelectedChange}
            />
          )}
        </div>
      </div>
      <VehicleEditCard
        vehicleId={null}
        show={showAddNewModal}
        onSuccess={handleEditSuccess}
        onClose={() => setShowAddNewModal(false)}
      />
      <VehicleExportModal
        show={showExportModal}
        selectedVehicleIds={selectedRows}
        vehicles={Object.values(selectedVehiclesMap)}
        onClose={() => setShowExportModal(false)}
        onExportSuccess={handleExportSuccess}
      />
      <VehicleImportModal
        show={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImportSuccess={handleImportSuccess}
      />
      <VehicleJomImportModal
        show={showJomImportModal}
        onClose={() => setShowJomImportModal(false)}
        onImportSuccess={handleJomImportSuccess}
      />
      <VehicleResidualValueModal
        show={showResidualValueModal}
        onClose={() => setShowResidualValueModal(false)}
      />
      <VehicleNewPriceModal
        show={showNewPriceModal}
        onClose={() => setShowNewPriceModal(false)}
      />
      <VehicleUsedPriceModal
        show={showUsedPriceModal}
        onClose={() => setShowUsedPriceModal(false)}
      />
    </>
  );
};

VehicleSearchPage.route = Routes.FleetVehicleSearchPageRouter;

export default VehicleSearchPage;